import { DocumentNode, gql } from '@apollo/client'
import { Button, ButtonProps } from '@farol-ds/react'
import { forwardRef, useCallback, useContext, useEffect, useState } from 'react'

import { BoxLockContext } from '../context'
import { SubscribeButton_OfferLockCardFragment } from './__generated__/subscribe-button.graphql'

export type SubscribeButtonProps = Omit<
  ButtonProps,
  'children' | 'type' | 'loading' | 'disabled'
> & {
  offerLockCard: SubscribeButton_OfferLockCardFragment
  onClick?: () => void
  forceDisabled?: boolean
}

const SubscribeButtonWithRef = forwardRef<HTMLButtonElement, SubscribeButtonProps>(
  (props, forwardedRef) => {
    const { offerLockCard, kind = 'secondary', onClick, forceDisabled, ...rest } = props
    const { status, interactive } = useContext(BoxLockContext)
    const label =
      status === 'error' ? offerLockCard.button.labelOnError : offerLockCard.button.label
    const type = onClick ? 'button' : 'submit'
    const [loading, setLoading] = useState(false)

    const onClickWrapper = useCallback(() => {
      setLoading(true)
      onClick?.()
    }, [onClick])

    useEffect(() => {
      if (interactive || status === 'success') {
        setLoading(false)
      }
    }, [interactive, status])

    return (
      <Button
        block
        {...rest}
        type={type}
        onClick={onClickWrapper}
        kind={kind}
        loading={loading}
        disabled={!interactive || forceDisabled}
        ref={forwardedRef}
      >
        {label}
      </Button>
    )
  }
)

SubscribeButtonWithRef.displayName = 'SubscribeButton'

type SubscribeButtonWithRefType = typeof SubscribeButtonWithRef
interface SubscribeButton extends SubscribeButtonWithRefType {
  fragments: Record<string, DocumentNode>
}

export const SubscribeButton: SubscribeButton = Object.assign<
  SubscribeButtonWithRefType,
  Pick<SubscribeButton, 'fragments'>
>(SubscribeButtonWithRef, {
  fragments: {
    offerLockCard: gql`
      fragment SubscribeButton_offerLockCard on OfferLockCard {
        button {
          label
          labelOnError
        }
      }
    `,
  },
})
